import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy-load all pages to reduce initial bundle size
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ReviewHistory = lazy(() => import('./pages/ReviewHistory'));
const ReviewDetail = lazy(() => import('./pages/ReviewDetail'));
const Installations = lazy(() => import('./pages/Installations'));
const RepoSettings = lazy(() => import('./pages/RepoSettings'));
const InstallationSettings = lazy(() => import('./pages/InstallationSettings'));
const Usage = lazy(() => import('./pages/Usage'));
const Layout = lazy(() => import('./components/Layout'));

const PageFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-bg">
    <LoadingSpinner size="lg" />
  </div>
);

function App() {
  return (
    // ThemeProvider is outermost so CSS variables are applied before first paint
    <ThemeProvider>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
            },
            success: { iconTheme: { primary: 'var(--color-success)', secondary: 'var(--color-surface)' } },
            error: { iconTheme: { primary: 'var(--color-error)', secondary: 'var(--color-surface)' } },
          }}
        />
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Protected routes — require authenticated session */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    <Route path="/dashboard" element={
                      <ErrorBoundary><Dashboard /></ErrorBoundary>
                    } />
                    <Route path="/reviews" element={
                      <ErrorBoundary><ReviewHistory /></ErrorBoundary>
                    } />
                    <Route path="/reviews/:id" element={
                      <ErrorBoundary><ReviewDetail /></ErrorBoundary>
                    } />
                    <Route path="/installations" element={
                      <ErrorBoundary><Installations /></ErrorBoundary>
                    } />
                    <Route path="/settings/installations/:id" element={
                      <ErrorBoundary><InstallationSettings /></ErrorBoundary>
                    } />
                    <Route path="/settings/repos/:id" element={
                      <ErrorBoundary><RepoSettings /></ErrorBoundary>
                    } />
                    <Route path="/usage" element={
                      <ErrorBoundary><Usage /></ErrorBoundary>
                    } />
                  </Route>
                </Route>

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
